import { useEffect, useState } from "react";
import { useSnapshot } from "valtio";
import { toast } from "sonner";
import {
  adminStore,
  fetchScopes,
  fetchRoles,
  type Scope,
} from "../stores/admin";
import { tx, transact, id } from "@/lib/instantdb";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { AlertCircle, Plus, Pencil, Trash2, Shield, Lock } from "lucide-react";

interface ScopeFormData {
  name: string;
  description: string;
}

const defaultFormData: ScopeFormData = {
  name: "",
  description: "",
};

function ScopeDialog({
  open,
  onOpenChange,
  scope,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scope: Scope | null;
  onSubmit: (data: ScopeFormData) => Promise<void>;
}) {
  const [formData, setFormData] = useState<ScopeFormData>(defaultFormData);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (scope) {
      setFormData({
        name: scope.name,
        description: scope.description ?? "",
      });
    } else {
      setFormData(defaultFormData);
    }
  }, [scope, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit(formData);
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  const isEdit = !!scope;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Scope" : "Create Scope"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? `Update the configuration for ${scope.name}`
              : "Create a new permission scope"}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Scope Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="users:read"
              disabled={isEdit}
              required
            />
            <p className="text-xs text-muted-foreground">
              Use format: resource:action (e.g., users:read, projects:write)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Read access to user data"
              required
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Saving..." : isEdit ? "Update" : "Create"} Scope
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Scopes() {
  const { scopes, scopesLoading, roles, error } = useSnapshot(adminStore);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingScope, setEditingScope] = useState<Scope | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [scopeToDelete, setScopeToDelete] = useState<Scope | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchScopes();
    fetchRoles();
  }, []);

  // Count which roles use each scope
  const getScopeUsageCount = (scopeId: string): number => {
    return roles.filter((role) => role.scopes?.some((s) => s.id === scopeId))
      .length;
  };

  const getRolesUsingScope = (scopeId: string): string[] => {
    return roles
      .filter((role) => role.scopes?.some((s) => s.id === scopeId))
      .map((role) => role.name);
  };

  const handleCreateScope = async (data: ScopeFormData) => {
    try {
      const scopeId = id();
      await transact(
        tx.admin_scopes[scopeId].update({
          name: data.name,
          description: data.description,
        }),
      );
      toast.success("Scope created successfully");
      await fetchScopes(true);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to create scope");
      throw e;
    }
  };

  const handleUpdateScope = async (data: ScopeFormData) => {
    if (!editingScope) return;
    try {
      await transact(
        tx.admin_scopes[editingScope.id].update({
          description: data.description,
        }),
      );
      toast.success("Scope updated successfully");
      await fetchScopes(true);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to update scope");
      throw e;
    }
  };

  const handleDeleteScope = async () => {
    if (!scopeToDelete) return;

    // Check if scope is in use
    const usageCount = getScopeUsageCount(scopeToDelete.id);
    if (usageCount > 0) {
      toast.error(`Cannot delete scope: used by ${usageCount} role(s)`);
      setDeleteDialogOpen(false);
      return;
    }

    setDeleteLoading(true);
    try {
      await transact(tx.admin_scopes[scopeToDelete.id].delete());
      toast.success("Scope deleted successfully");
      setDeleteDialogOpen(false);
      setScopeToDelete(null);
      await fetchScopes(true);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete scope");
    } finally {
      setDeleteLoading(false);
    }
  };

  const openEditDialog = (scope: Scope) => {
    setEditingScope(scope);
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingScope(null);
    setDialogOpen(true);
  };

  const openDeleteDialog = (scope: Scope) => {
    setScopeToDelete(scope);
    setDeleteDialogOpen(true);
  };

  return (
    <div className="flex h-full flex-col">
      {/* Fixed Header */}
      <header className="shrink-0 border-b bg-background px-4 py-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Scopes</h1>
            <p className="mt-2 text-muted-foreground">
              Manage permission scopes for role-based access control
            </p>
          </div>
          <Button onClick={openCreateDialog}>
            <Plus className="h-4 w-4 mr-2" />
            Create Scope
          </Button>
        </div>
      </header>

      {/* Scrollable Content */}
      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          {/* Error State */}
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Scopes Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                All Scopes ({scopes.length})
              </CardTitle>
              <CardDescription>
                Scopes define granular permissions that can be assigned to roles
              </CardDescription>
            </CardHeader>
            <CardContent>
              {scopesLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="h-12" />
                  ))}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Used By</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {scopes.length > 0 ? (
                      scopes.map((scope) => {
                        const rolesUsing = getRolesUsingScope(scope.id);
                        return (
                          <TableRow key={scope.id}>
                            <TableCell>
                              <code className="text-sm font-medium bg-muted px-2 py-1 rounded">
                                {scope.name}
                              </code>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {scope.description || "-"}
                            </TableCell>
                            <TableCell>
                              {rolesUsing.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {rolesUsing.slice(0, 3).map((roleName) => (
                                    <Badge key={roleName} variant="secondary">
                                      <Shield className="h-3 w-3 mr-1" />
                                      {roleName}
                                    </Badge>
                                  ))}
                                  {rolesUsing.length > 3 && (
                                    <Badge variant="outline">
                                      +{rolesUsing.length - 3}
                                    </Badge>
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted-foreground">
                                  Not in use
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditDialog(scope as Scope)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-destructive"
                                  onClick={() =>
                                    openDeleteDialog(scope as Scope)
                                  }
                                  disabled={rolesUsing.length > 0}
                                  title={
                                    rolesUsing.length > 0
                                      ? "Cannot delete scope in use"
                                      : undefined
                                  }
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          className="text-center text-muted-foreground py-8"
                        >
                          No scopes found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Scope Dialog */}
      <ScopeDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditingScope(null);
        }}
        scope={editingScope}
        onSubmit={editingScope ? handleUpdateScope : handleCreateScope}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Scope"
        description={`Are you sure you want to delete the scope "${scopeToDelete?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDeleteScope}
        loading={deleteLoading}
      />
    </div>
  );
}
